import { forwardRef, useId } from 'react';
import clsx from 'clsx';

/**
 * Labelled form field. 16px font on inputs is enforced globally in index.css so
 * focusing a field never zooms the page on iOS.
 */
export const TextField = forwardRef(function TextField(
  { label, error, hint, required, className, id, ...props },
  ref,
) {
  const autoId = useId();
  const fieldId = id ?? autoId;

  return (
    <div className={clsx('flex flex-col gap-1.5', className)}>
      {label ? (
        <label htmlFor={fieldId} className="text-sm font-medium text-content">
          {label}
          {required ? <span className="text-danger"> *</span> : null}
        </label>
      ) : null}
      <input
        ref={ref}
        id={fieldId}
        aria-invalid={!!error}
        aria-describedby={error ? `${fieldId}-error` : hint ? `${fieldId}-hint` : undefined}
        className={clsx(
          'h-touch w-full rounded-card border bg-surface px-3.5 text-content placeholder:text-muted',
          error ? 'border-danger' : 'border-border',
        )}
        {...props}
      />
      {error ? (
        <p id={`${fieldId}-error`} className="text-sm text-danger">
          {error}
        </p>
      ) : hint ? (
        <p id={`${fieldId}-hint`} className="text-sm text-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
});

export const SelectField = forwardRef(function SelectField(
  { label, error, hint, required, className, id, children, ...props },
  ref,
) {
  const autoId = useId();
  const fieldId = id ?? autoId;

  return (
    <div className={clsx('flex flex-col gap-1.5', className)}>
      {label ? (
        <label htmlFor={fieldId} className="text-sm font-medium text-content">
          {label}
          {required ? <span className="text-danger"> *</span> : null}
        </label>
      ) : null}
      <select
        ref={ref}
        id={fieldId}
        aria-invalid={!!error}
        className={clsx(
          'h-touch w-full rounded-card border bg-surface px-3.5 text-content',
          error ? 'border-danger' : 'border-border',
        )}
        {...props}
      >
        {children}
      </select>
      {error ? <p className="text-sm text-danger">{error}</p> : hint ? <p className="text-sm text-muted">{hint}</p> : null}
    </div>
  );
});

export const TextAreaField = forwardRef(function TextAreaField(
  { label, error, hint, className, id, rows = 3, ...props },
  ref,
) {
  const autoId = useId();
  const fieldId = id ?? autoId;

  return (
    <div className={clsx('flex flex-col gap-1.5', className)}>
      {label ? (
        <label htmlFor={fieldId} className="text-sm font-medium text-content">
          {label}
        </label>
      ) : null}
      <textarea
        ref={ref}
        id={fieldId}
        rows={rows}
        className={clsx(
          'w-full rounded-card border bg-surface px-3.5 py-2.5 text-content placeholder:text-muted',
          error ? 'border-danger' : 'border-border',
        )}
        {...props}
      />
      {error ? <p className="text-sm text-danger">{error}</p> : hint ? <p className="text-sm text-muted">{hint}</p> : null}
    </div>
  );
});
